import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { UsedFieldsContext } from "@/context/usedfields.context";
import { cn } from "@/lib/utils";
import { FormFieldProps } from "@/types/form";
import { Cross2Icon } from "@radix-ui/react-icons";
import { startCase } from "lodash";
import { CalendarIcon } from "lucide-react";
import { useContext, useEffect } from "react";
import { ControllerRenderProps, FieldValues, useFormContext } from "react-hook-form";
import { z } from "zod";
import MultipleSelector from "../../ui/multiselect";

function SwimFormField<
    TSchema extends z.AnyZodObject
>({
    schema,
    fieldName,
    label,
    description,
    placeholder,
    required,
    priceField = false,
    className,
    disabled = false
}: FormFieldProps<TSchema>) {

    const { control } = useFormContext();
    const schemaField = schema.shape[fieldName];
    const usedFields = useContext(UsedFieldsContext);

    useEffect(() => {
        if (schemaField && usedFields && !usedFields.current.includes(fieldName)) {
            usedFields.current.push(fieldName);
            return () => {
                usedFields.current = usedFields.current.filter(f => f !== fieldName);
            }
        }
    }, [schemaField, fieldName, usedFields]);

    if (!schemaField) { return null; }

    className = cn(
        "text-sm sm:text-base lg:text-lg h-10 sm:h-11 lg:h-12",
        className
    );


    function unwrapSchema(schema: z.ZodTypeAny): z.ZodTypeAny {
        if (!description && schema.description) {
            description = schema.description
        }
        if (schema instanceof z.ZodOptional ||
            schema instanceof z.ZodNullable) {
            return unwrapSchema(schema.unwrap());
        }
        if (schema instanceof z.ZodDefault) {
            return unwrapSchema(schema._def.innerType);
        }
        if (schema instanceof z.ZodEffects) {
            return unwrapSchema(schema._def.schema);
        }
        return schema;
    }

    const innerSchema = unwrapSchema(schemaField);

    const supportedFieldTypes = [
        z.ZodString,
        z.ZodNumber,
        z.ZodBoolean,
        z.ZodDate,
        z.ZodEnum,
        z.ZodArray
    ];


    const isSupported = supportedFieldTypes.some(type => innerSchema instanceof type);

    if (!isSupported) {
        console.error('Unsupported field type:', innerSchema);
        throw new Error(`Unsupported field type: ${innerSchema.constructor.name}`);
    }

    if (innerSchema instanceof z.ZodArray) {
        const arrayField = innerSchema._def.type;
        if (!(arrayField instanceof z.ZodEnum)) {
            console.error('Unsupported array field type:', arrayField);
            throw new Error(`Unsupported array field type: ${arrayField.constructor.name}`);
        }
    }

    // Only set required if it's not explicitly provided
    if (required === undefined) {
        required = !(schemaField.isOptional() || schemaField.isNullable()) && (!(innerSchema instanceof z.ZodArray) || Boolean(innerSchema._def.minLength));
    }

    function renderField(field: ControllerRenderProps<FieldValues, string>) {
        if (innerSchema instanceof z.ZodString) {
            return (innerSchema.maxLength ?? 0) > 300 ? (
                <Textarea
                    placeholder={placeholder}
                    {...field}
                    value={field.value ?? ''}
                    className={className}
                    disabled={disabled}
                />
            ) : (
                <Input
                    placeholder={placeholder}
                    {...field}
                    value={field.value ?? ''}
                    type={schemaField.isEmail ? 'email' : 'text'}
                    className={className}
                    disabled={disabled}
                />
            );
        }
        if (innerSchema instanceof z.ZodNumber) {
            return !priceField ? (
                <Input
                    type="number"
                    min={innerSchema.minValue ?? undefined}
                    max={innerSchema.maxValue ?? undefined}
                    placeholder={placeholder}
                    step={innerSchema._def.checks.find((check) => check.kind === 'multipleOf')?.value ?? 1}
                    {...field}
                    value={field.value ?? ''}
                    onChange={(e) => { field.onChange(parseFloat(e.target.value)); }}
                    className={className}
                    disabled={disabled}
                />
            ) : (
                <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2">$</span>
                    <Input
                        type="number"
                        min={innerSchema.minValue ?? 0}
                        max={innerSchema.maxValue ?? undefined}
                        step="0.01"
                        placeholder={placeholder}
                        {...field}
                        value={field.value ?? ''}
                        onChange={(e) => { field.onChange(parseFloat(e.target.value)); }}
                        className={cn("pl-7", className)}
                        disabled={disabled}
                    />
                </div>
            );
        }
        if (innerSchema instanceof z.ZodBoolean) {
            return (
                <div className="flex gap-4">
                    <button
                        type="button"
                        onClick={() => !disabled && field.onChange(true)}
                        className={cn(
                            "w-full p-3 rounded-lg border transition-all duration-150 cursor-pointer",
                            "hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1",
                            "flex items-center justify-center text-center",
                            field.value === true
                                ? "border-primary bg-primary text-primary-foreground font-medium"
                                : "border-gray-200 bg-white hover:border-gray-300 text-gray-700",
                            disabled && "opacity-50 pointer-events-none"
                        )}
                        disabled={disabled}
                    >
                        Yes
                    </button>
                    <button
                        type="button"
                        onClick={() => !disabled && field.onChange(false)}
                        className={cn(
                            "w-full p-3 rounded-lg border transition-all duration-150 cursor-pointer",
                            "hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1",
                            "flex items-center justify-center text-center",
                            field.value === false
                                ? "border-primary bg-primary text-primary-foreground font-medium"
                                : "border-gray-200 bg-white hover:border-gray-300 text-gray-700",
                            disabled && "opacity-50 pointer-events-none"
                        )}
                        disabled={disabled}
                    >
                        No
                    </button>
                </div>
            );
        }
        if (innerSchema instanceof z.ZodDate) {
            return (
                <Popover>
                    <PopoverTrigger asChild>
                        <Button
                            variant={"outline"}
                            className={className}
                            disabled={disabled}
                        >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {field.value ? new Date(field.value).toLocaleDateString() : "Select a date"}
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                        <Calendar
                            mode="single"
                            selected={new Date(field.value)}
                            onSelect={field.onChange}
                            autoFocus
                            disabled={disabled}
                        />
                    </PopoverContent>
                </Popover>
            );
        }
        if (innerSchema instanceof z.ZodEnum) {
            return (
                <Select
                    onValueChange={(value) => {
                        if (!disabled) {
                            const isValidOption = innerSchema.options.includes(value);
                            field.onChange(isValidOption ? value : null);
                        }
                    }}
                    value={field.value ?? ""}
                    disabled={disabled}
                >
                    <div className="relative w-full">
                        <SelectTrigger
                            className={cn(
                                className,
                                "nice-big-input w-full text-ellipsis overflow-hidden whitespace-nowrap cursor-pointer",
                                !field.value && "text-gray-400"
                            )}
                            disabled={disabled}
                        >
                            <SelectValue
                                placeholder={
                                    (() => {
                                        const options = innerSchema.options.join(', ');
                                        return options.length > 25 ? options.substring(0, 25) + '...' : options;
                                    })()
                                }
                            />
                        </SelectTrigger>
                        {field.value && !disabled && (
                            <Button
                                type="button"
                                size="icon"
                                variant="ghost"
                                aria-label="Clear selection"
                                className="absolute right-8 top-1/2 -translate-y-1/2 z-10 p-1 rounded cursor-pointer"
                                onClick={() => field.onChange(null)}
                            >
                                <Cross2Icon className="h-4 w-4 items-center justify-center rounded-sm opacity-50 hover:opacity-100 focus:opacity-100" />
                            </Button>
                        )}
                    </div>
                    <SelectContent>
                        {innerSchema.options.map((type: string) => (
                            <SelectItem key={type} value={type} className={className} disabled={disabled}>
                                {type}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            );
        }
        if ((innerSchema instanceof z.ZodArray) && (innerSchema._def.type instanceof z.ZodEnum)) {
            return (
                <MultipleSelector
                    value={field.value?.map((v: string) => ({ label: v, value: v })) ?? []}
                    onChange={(options) => !disabled && field.onChange(options.map(opt => opt.value))}
                    defaultOptions={innerSchema._def.type.options.map((type: string) => ({ label: type, value: type }))}
                    placeholder={placeholder}
                    maxSelected={innerSchema._def.maxLength?.value}
                    hidePlaceholderWhenSelected
                    className={cn("text-sm sm:text-base lg:text-lg")}
                    disabled={disabled}
                />
            );
        }
        return null;
    }

    return (<FormField
        control={control}
        name={fieldName as string}
        render={({ field }) => (
            <FormItem className="flex flex-col justify-between h-full" data-field-name={fieldName}>
                <FormLabel aria-required={required} className={cn({
                    "after:content-['*'] after:ml-0.5 after:text-red-500": required
                })}>
                    {label || startCase(fieldName.replace(/_/g, ' '))}
                </FormLabel>
                <FormDescription>
                    {description}
                </FormDescription>
                <FormControl>
                    {renderField(field)}
                </FormControl>
                <FormMessage />
            </FormItem>
        )}
    />);
}

export default SwimFormField;